import React, { useState } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Avatar,
  Fade,
  Zoom,
  IconButton,
  Paper,
  LinearProgress,
  Stack,
  alpha,
  Autocomplete,
  TextField,
  CircularProgress,
  Alert,
  Chip,
  Divider
} from '@mui/material';

import civiqLogo from '../img/civiq.jpg';

import {
  Close as CloseIcon,
  AccountBox as ProfileIcon,
  People as DemographicsIcon,
  Timeline as HistoricalIcon,
  TrendingUp as TrendsIcon,
  Article as NewsIcon,
  Group as PartyIcon,
  Assessment as EconomyIcon,
  Psychology as ForecastIcon,
  Compare as CompareIcon,
  ArrowUpward,
  ThumbUp,
  ThumbDown,
  TrendingFlat,
  AutoAwesome as AIIcon,
  Lightbulb as NextStepsIcon,
  Chat as ChatIcon,
  Send as SendIcon,
  SmartToy as BotIcon
} from '@mui/icons-material';

// Import custom hooks and utilities
import { useConstituencyData } from '../hooks/useConstituencyData';
import { formatNumber, formatPercentage, generateMockOpinionTrends, generateMockSocialMediaData, generateMockPartyStrength, generateMockEconomicData, generateMockForecasts } from '../utils/dataUtils';
import { ProfileModal, DemographicsModal, VotingModal, ResultsModal, CandidatesModal, InfrastructureModal, NewsModal } from './ModalComponents';
import constituencyService from '../services/constituencyService';

// Color palettes for charts
const COLORS = ['#8884d8', '#82ca9d', '#ffc658', '#ff7300', '#0088fe'];
const SENTIMENT_COLORS = { positive: '#4caf50', neutral: '#ff9800', negative: '#f44336' };

const ConstituencyDashboard = () => {
  const [openModal, setOpenModal] = useState(null);
  const [detailedResults, setDetailedResults] = useState(null);
  const [loadingDetailedResults, setLoadingDetailedResults] = useState(false);
  const [chatMessages, setChatMessages] = useState([
    {
      id: 1,
      type: 'bot',
      content: 'Hello! I\'m your AI assistant for constituency analysis. Ask me anything about the electoral data, demographics, or political insights.',
      timestamp: new Date()
    }
  ]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  
  // AI-powered insights state
  const [aiInsights, setAiInsights] = useState(null);
  const [aiPredictions, setAiPredictions] = useState(null);
  const [aiRecommendations, setAiRecommendations] = useState(null);
  const [loadingAiInsights, setLoadingAiInsights] = useState(false);
  const [loadingAiPredictions, setLoadingAiPredictions] = useState(false);
  const [loadingAiRecommendations, setLoadingAiRecommendations] = useState(false);
  
  // Use the custom hook for constituency data
  const {
    loading,
    error,
    constituencies,
    selectedConstituency,
    currentData,
    changeConstituency
  } = useConstituencyData();

  // Load detailed results when constituency changes
  React.useEffect(() => {
    if (selectedConstituency && currentData && !loadingDetailedResults) {
      loadDetailedResults();
    }
  }, [selectedConstituency, currentData]);

  const loadDetailedResults = async () => {
    if (!selectedConstituency || !currentData) return;
    
    setLoadingDetailedResults(true);
    try {
      const constituencyName = currentData.profile?.name;
      if (constituencyName) {
        const detailed = await constituencyService.getDetailedResults(constituencyName);
        setDetailedResults(detailed);
      }
    } catch (error) {
      console.error('Failed to load detailed results:', error);
      setDetailedResults(null);
    } finally {
      setLoadingDetailedResults(false);
    }
  };

  // Load AI-powered insights
  const loadAiInsights = async () => {
    if (!selectedConstituency || !currentData) return;
    
    setLoadingAiInsights(true);
    try {
      const constituencyName = currentData.profile?.name;
      if (constituencyName) {
        // Use the comprehensive analysis endpoint instead of quick-summary
        const response = await fetch(`http://localhost:8001/analyze`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            constituency_name: constituencyName,
            analysis_type: 'comprehensive',
            focus_areas: ['voter_turnout', 'party_performance', 'demographic_analysis'],
            time_horizon: '6_months'
          })
        });
        if (response.ok) {
          const insights = await response.json();
          setAiInsights(insights);
        }
      }
    } catch (error) {
      console.error('Failed to load AI insights:', error);
      setAiInsights(null);
    } finally {
      setLoadingAiInsights(false);
    }
  };

  // Load AI predictions
  const loadAiPredictions = async () => {
    if (!selectedConstituency || !currentData) return;
    
    setLoadingAiPredictions(true);
    try {
      const constituencyName = currentData.profile?.name;
      if (constituencyName) {
        const response = await fetch(`http://localhost:8001/analyze`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            constituency_name: constituencyName,
            analysis_type: 'prediction',
            focus_areas: ['electoral_trends', 'voter_behavior'],
            time_horizon: '12_months'
          })
        });
        if (response.ok) {
          const predictions = await response.json();
          setAiPredictions(predictions);
        }
      }
    } catch (error) {
      console.error('Failed to load AI predictions:', error);
      setAiPredictions(null);
    } finally {
      setLoadingAiPredictions(false);
    }
  };

  // Load AI recommendations/strategy
  const loadAiRecommendations = async () => {
    if (!selectedConstituency || !currentData) return;
    
    setLoadingAiRecommendations(true);
    try {
      const constituencyName = currentData.profile?.name;
      if (constituencyName) {
        const response = await fetch(`http://localhost:8001/analyze`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            constituency_name: constituencyName,
            analysis_type: 'strategy',
            focus_areas: ['voter_engagement', 'digital_outreach', 'policy_development', 'campaign_strategy'],
            time_horizon: '6_months'
          })
        });
        if (response.ok) {
          const recommendations = await response.json();
          setAiRecommendations(recommendations);
        }
      }
    } catch (error) {
      console.error('Failed to load AI recommendations:', error);
      setAiRecommendations(null);
    } finally {
      setLoadingAiRecommendations(false);
    }
  };

  // Generate enhanced data for display
  const enhancedData = React.useMemo(() => {
    if (!currentData) return null;
    
    // Enhance with mock data for features not in the election data
    const constituency = constituencies.find(c => c.code === selectedConstituency);
    
    return {
      ...currentData,
      opinionTrends: generateMockOpinionTrends(constituency),
      socialMedia: generateMockSocialMediaData(constituency),
      partyStrength: generateMockPartyStrength(currentData.results?.winner?.party),
      economy: generateMockEconomicData(constituency),
      forecasts: generateMockForecasts(constituency, generateMockPartyStrength(currentData.results?.winner?.party)),
      news: [
        { title: `Latest developments in ${constituency?.fullName || 'constituency'}`, sentiment: "Positive", date: "2025-08-10" },
        { title: `Voter awareness campaign launched`, sentiment: "Positive", date: "2025-08-08" }
      ]
    };
  }, [currentData, constituencies, selectedConstituency]);

  const handleConstituencyChange = (event, newValue) => {
    if (newValue) {
      changeConstituency(newValue.code);
    }
  };

  const handleTileClick = (modalType) => {
    setOpenModal(modalType);
  };

  const handleCloseModal = () => {
    setOpenModal(null);
  };

  const getSentimentColor = (sentiment) => {
    return SENTIMENT_COLORS[sentiment.toLowerCase()] || '#757575';
  };

  // Chat functionality
  const handleChatSubmit = async () => {
    if (!chatInput.trim() || chatLoading) return;

    const userMessage = {
      id: Date.now(),
      type: 'user',
      content: chatInput,
      timestamp: new Date()
    };

    setChatMessages(prev => [...prev, userMessage]);
    setChatInput('');
    setChatLoading(true);

    // Simulate AI response with constituency-specific insights
    setTimeout(() => {
      const aiResponse = generateAIResponse(chatInput, enhancedData);
      const botMessage = {
        id: Date.now() + 1,
        type: 'bot',
        content: aiResponse,
        timestamp: new Date()
      };
      setChatMessages(prev => [...prev, botMessage]);
      setChatLoading(false);
    }, 1500);
  };

  const generateAIResponse = (question, data) => {
    const responses = [
      `Based on the data for ${data?.profile?.name || 'this constituency'}, I can see interesting patterns in voter demographics and turnout.`,
      `The constituency shows a ${data?.voting?.turnout?.percentage || 65}% voter turnout, which is ${data?.voting?.turnout?.percentage > 70 ? 'above' : 'near'} the national average.`,
      `Key demographic insights: The literacy rate of ${data?.profile?.voters?.literacyRate || 70}% suggests ${data?.profile?.voters?.literacyRate > 75 ? 'high' : 'moderate'} educational engagement.`,
      `Political trends indicate ${data?.results?.winner?.party || 'strong competition'} ${data?.results?.winner ? 'maintaining leadership' : 'among major parties'} in this region.`,
      `The economic profile shows promising development indicators with infrastructure improvements in progress.`
    ];
    
    return responses[Math.floor(Math.random() * responses.length)];
  };

  // Render modal content based on type
  const renderModalContent = () => {
    if (!enhancedData) return <Typography>No data available</Typography>;
    
    switch (openModal) {
      case 'profile':
        return <ProfileModal data={enhancedData} />;
      case 'demographics':
        return <DemographicsModal data={enhancedData} />;
      case 'voting':
        return <VotingModal data={enhancedData} />;
      case 'results':
        return <ResultsModal data={enhancedData} />;
      case 'candidates':
        return <CandidatesModal data={enhancedData} />;
      case 'infrastructure':
        return <InfrastructureModal data={enhancedData} />;
      case 'opinion':
        return (
          <Box sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>Opinion Trends Analysis</Typography>
            <Typography variant="body1" paragraph>
              Current sentiment analysis shows public opinion trends for this constituency.
            </Typography>
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="success.main">
                • Positive feedback on infrastructure development
              </Typography>
              <Typography variant="body2" color="warning.main">
                • Mixed opinions on employment opportunities
              </Typography>
              <Typography variant="body2" color="error.main">
                • Concerns about healthcare facilities
              </Typography>
            </Box>
          </Box>
        );
      case 'news':
        return <NewsModal data={enhancedData} />;
      case 'economy':
        return (
          <Box sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>Economic Profile</Typography>
            <Typography variant="body1" paragraph>
              Economic indicators and development metrics for this constituency.
            </Typography>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={6}>
                <Typography variant="body2"><strong>Average Income:</strong> ₹{enhancedData.economy?.avgIncome || '18,500'}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2"><strong>Employment Rate:</strong> {enhancedData.economy?.employmentRate || '64'}%</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2"><strong>Primary Sector:</strong> {enhancedData.economy?.primarySector || 'Agriculture (58%)'}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2"><strong>Growth Rate:</strong> {enhancedData.economy?.growthRate || '+3.2%'}</Typography>
              </Grid>
            </Grid>
          </Box>
        );
      default:
        return <Typography>Select a category to view details</Typography>;
    }
  };

  // Show loading state
  if (loading && !enhancedData) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ ml: 2 }}>Loading constituency data...</Typography>
      </Box>
    );
  }

  // Show error state
  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Typography variant="body1">
          Please check your connection and try refreshing the page.
        </Typography>
      </Box>
    );
  }

  // Show no data state
  if (!enhancedData) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">
          No data available for the selected constituency.
        </Alert>
      </Box>
    );
  }

  // Tile components with enhanced data and styling - Clean professional design
  const tiles = [
    {
      id: 'profile',
      title: 'Constituency Profile',
      icon: <ProfileIcon />,
      color: '#2563eb',
      gradient: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
      summary: (
        <Box>
          <Stack spacing={2}>
            <Box>
              <Typography variant="h5" fontWeight="bold">
                {enhancedData.profile.name}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.8 }}>
                {enhancedData.profile.code} • {enhancedData.profile.state}
              </Typography>
            </Box>
            
            <Grid container spacing={1}>
              <Grid item xs={4}>
                <Box textAlign="center">
                  <Typography variant="h6" fontWeight="bold">
                    {formatNumber(enhancedData.profile.voters.total)}
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.7 }}>
                    Total Voters
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={4}>
                <Box textAlign="center">
                  <Typography variant="h6" fontWeight="bold">
                    {enhancedData.profile.voters.literacyRate}%
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.7 }}>
                    Literacy
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={4}>
                <Box textAlign="center">
                  <Typography variant="h6" fontWeight="bold">
                    {enhancedData.profile.reservedStatus}
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.7 }}>
                    Status
                  </Typography>
                </Box>
              </Grid>
            </Grid>

            <Box>
              <Typography variant="body2" sx={{ opacity: 0.8, fontSize: '0.85rem' }}>
                M: {formatNumber(enhancedData.profile.voters.male)} • F: {formatNumber(enhancedData.profile.voters.female)}
              </Typography>
            </Box>
          </Stack>
        </Box>
      )
    },
    {
      id: 'demographics',
      title: 'Demographics',
      icon: <DemographicsIcon />,
      color: '#059669',
      gradient: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
      summary: (
        <Box>
          <Stack spacing={2}>
            <Box>
              <Typography variant="h6" fontWeight="bold" color="white">
                Population Breakdown
              </Typography>
            </Box>
            
            {Object.entries(enhancedData.demographics.caste)
              .sort(([,a], [,b]) => b - a)
              .slice(0, 3)
              .map(([group, percentage], index) => (
                <Box key={group} sx={{ mb: 1 }}>
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                      {group}
                    </Typography>
                    <Typography variant="body2" fontWeight="bold" color="white">
                      {percentage}%
                    </Typography>
                  </Box>
                  <LinearProgress 
                    variant="determinate" 
                    value={percentage} 
                    sx={{ 
                      mt: 0.5,
                      height: 6,
                      borderRadius: 3,
                      backgroundColor: 'rgba(255,255,255,0.2)',
                      '& .MuiLinearProgress-bar': {
                        backgroundColor: index === 0 ? '#fff' : 'rgba(255,255,255,0.8)'
                      }
                    }} 
                  />
                </Box>
              ))}
            
            <Box sx={{ pt: 1, borderTop: '1px solid rgba(255,255,255,0.2)' }}>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                Dominant Age Group: 26-40 ({enhancedData.demographics.ageGroups["26-40"]}%)
              </Typography>
            </Box>
          </Stack>
        </Box>
      )
    },
    {
      id: 'voting',
      title: 'Voting Data',
      icon: <HistoricalIcon />,
      color: '#dc2626',
      gradient: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
      summary: (
        <Box>
          <Stack spacing={2}>
            <Box>
              <Typography variant="h6" fontWeight="bold" color="white">
                Voting Statistics
              </Typography>
            </Box>
            
            <Box>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                  Turnout
                </Typography>
                <Typography variant="h5" fontWeight="bold" color="white">
                  {enhancedData.voting ? `${enhancedData.voting.turnout.percentage}%` : 'N/A'}
                </Typography>
              </Box>
              
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                  Total Votes
                </Typography>
                <Typography variant="h6" fontWeight="bold" color="white">
                  {enhancedData.voting ? formatNumber(enhancedData.voting.turnout.total) : 'N/A'}
                </Typography>
              </Box>
              
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                  NOTA Votes
                </Typography>
                <Typography variant="body2" fontWeight="bold" color="white">
                  {enhancedData.voting ? formatNumber(enhancedData.voting.nota) : 'N/A'}
                </Typography>
              </Box>
            </Box>
            
            <Box sx={{ pt: 1, borderTop: '1px solid rgba(255,255,255,0.2)' }}>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                {enhancedData.voting && enhancedData.voting.turnout.percentage > 65 ? 
                 "Good turnout" : "Moderate turnout"}
              </Typography>
            </Box>
          </Stack>
        </Box>
      )
    },
    {
      id: 'results',
      title: 'Bihar 2020 Election Results',
      icon: <TrendsIcon />,
      color: '#7c3aed',
      gradient: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
      summary: (
        <Box>
          <Stack spacing={2}>
            <Box>
              <Typography variant="h6" fontWeight="bold" color="white">
                Election Outcome
              </Typography>
              {loadingDetailedResults && (
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                  Loading detailed results...
                </Typography>
              )}
            </Box>
            
            {detailedResults?.winner ? (
              <>
                <Box>
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                    Winner
                  </Typography>
                  <Typography variant="h6" fontWeight="bold" color="white">
                    {detailedResults.winner.party}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                    {detailedResults.winner.candidate_name}
                  </Typography>
                </Box>
                
                <Box>
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                    Votes & Share
                  </Typography>
                  <Typography variant="h6" fontWeight="bold" color="white">
                    {formatNumber(detailedResults.winner.votes)}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                    {detailedResults.winner.vote_percentage?.toFixed(1)}% vote share
                  </Typography>
                </Box>

                {detailedResults.victory_margin && (
                  <Box>
                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                      Victory Margin
                    </Typography>
                    <Typography variant="body1" fontWeight="bold" color="white">
                      {formatNumber(detailedResults.victory_margin.votes)} votes
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                      ({detailedResults.victory_margin.percentage?.toFixed(1)}% margin)
                    </Typography>
                  </Box>
                )}
              </>
            ) : enhancedData.results?.winner ? (
              <>
                <Box>
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                    Winner
                  </Typography>
                  <Typography variant="h6" fontWeight="bold" color="white">
                    {enhancedData.results.winner.party}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                    {enhancedData.results.winner.candidate}
                  </Typography>
                </Box>
                
                <Box>
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                    Votes Received
                  </Typography>
                  <Typography variant="h6" fontWeight="bold" color="white">
                    {formatNumber(enhancedData.results.winner.votes)}
                  </Typography>
                  {enhancedData.results.voteShare && (
                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                      ({enhancedData.results.voteShare}% vote share)
                    </Typography>
                  )}
                </Box>
              </>
            ) : (
              <Box>
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                  Results not available or election pending
                </Typography>
              </Box>
            )}
            
            <Box sx={{ pt: 1, borderTop: '1px solid rgba(255,255,255,0.2)' }}>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                {detailedResults?.turnout_summary ? 
                  `Turnout: ${detailedResults.turnout_summary.turnout_percentage?.toFixed(1)}% • ${detailedResults.candidates?.length || 0} candidates` :
                  'Click for detailed Bihar 2020 results'
                }
              </Typography>
            </Box>
          </Stack>
        </Box>
      )
    },
    {
      id: 'candidates',
      title: 'Candidate Analysis',
      icon: <CompareIcon />,
      color: '#ea580c',
      gradient: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
      summary: (
        <Box>
          <Stack spacing={2}>
            <Box>
              <Typography variant="h6" fontWeight="bold" color="white">
                Candidate Statistics
              </Typography>
            </Box>
            
            <Grid container spacing={1}>
              <Grid item xs={6}>
                <Box textAlign="center">
                  <Typography variant="h6" fontWeight="bold" color="white">
                    {enhancedData.candidates?.contested.total || 'N/A'}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                    Contested
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={6}>
                <Box textAlign="center">
                  <Typography variant="h6" fontWeight="bold" color="white">
                    {enhancedData.candidates?.genderBreakdown.women || 'N/A'}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                    Women
                  </Typography>
                </Box>
              </Grid>
            </Grid>
            
            <Box>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                Filed: {enhancedData.candidates?.filed.total || 'N/A'} • 
                Rejected: {enhancedData.candidates?.rejected.total || 'N/A'}
              </Typography>
            </Box>
            
            <Box sx={{ pt: 1, borderTop: '1px solid rgba(255,255,255,0.2)' }}>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                {enhancedData.candidates?.genderBreakdown.women > 0 ? 
                  `${Math.round((enhancedData.candidates.genderBreakdown.women / enhancedData.candidates.contested.total) * 100)}% women representation` :
                  'Gender breakdown available in details'
                }
              </Typography>
            </Box>
          </Stack>
        </Box>
      )
    },
    {
      id: 'infrastructure',
      title: 'Infrastructure',
      icon: <PartyIcon />,
      color: '#0891b2',
      gradient: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
      summary: (
        <Box>
          <Stack spacing={2}>
            <Box>
              <Typography variant="h6" fontWeight="bold" color="white">
                Polling Infrastructure
              </Typography>
            </Box>
            
            <Grid container spacing={1}>
              <Grid item xs={6}>
                <Box textAlign="center">
                  <Typography variant="h6" fontWeight="bold" color="white">
                    {enhancedData.infrastructure?.pollingStations.total || 'N/A'}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                    Polling Stations
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={6}>
                <Box textAlign="center">
                  <Typography variant="h6" fontWeight="bold" color="white">
                    {enhancedData.infrastructure?.pollingStations.averageElectors || 'N/A'}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                    Avg/Station
                  </Typography>
                </Box>
              </Grid>
            </Grid>
            
            <Box sx={{ pt: 1, borderTop: '1px solid rgba(255,255,255,0.2)' }}>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                {enhancedData.infrastructure?.pollingStations.averageElectors < 700 ? 
                  'Good coverage' : 
                  enhancedData.infrastructure?.pollingStations.averageElectors < 800 ?
                  'Adequate coverage' : 'High density'}
              </Typography>
            </Box>
          </Stack>
        </Box>
      )
    },
    {
      id: 'opinion',
      title: 'Opinion Trends',
      icon: <TrendsIcon />,
      color: '#0d9488',
      gradient: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
      summary: (
        <Box>
          <Stack spacing={2}>
            <Box>
              <Typography variant="h6" fontWeight="bold" color="white">
                Current Sentiment
              </Typography>
            </Box>
            
            <Grid container spacing={1}>
              <Grid item xs={4}>
                <Box textAlign="center">
                  <ThumbUp sx={{ color: '#4caf50', fontSize: '1.5rem' }} />
                  <Typography variant="h6" fontWeight="bold" color="white">
                    {enhancedData.opinionTrends?.positive || '45'}%
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                    Positive
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={4}>
                <Box textAlign="center">
                  <TrendingFlat sx={{ color: '#ff9800', fontSize: '1.5rem' }} />
                  <Typography variant="h6" fontWeight="bold" color="white">
                    {enhancedData.opinionTrends?.neutral || '35'}%
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                    Neutral
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={4}>
                <Box textAlign="center">
                  <ThumbDown sx={{ color: '#f44336', fontSize: '1.5rem' }} />
                  <Typography variant="h6" fontWeight="bold" color="white">
                    {enhancedData.opinionTrends?.negative || '20'}%
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                    Negative
                  </Typography>
                </Box>
              </Grid>
            </Grid>
            
            <Box sx={{ pt: 1, borderTop: '1px solid rgba(255,255,255,0.2)' }}>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                Trending: Development & Employment
              </Typography>
            </Box>
          </Stack>
        </Box>
      )
    },
    {
      id: 'news',
      title: 'News & Media',
      icon: <NewsIcon />,
      color: '#f97316',
      gradient: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
      summary: (
        <Box>
          <Stack spacing={2}>
            <Box>
              <Typography variant="h6" fontWeight="bold" color="white">
                Recent Coverage
              </Typography>
            </Box>
            
            <Box>
              <Typography variant="body2" sx={{ 
                color: 'rgba(255,255,255,0.95)', 
                fontWeight: 500,
                lineHeight: 1.3 
              }}>
                📰 Voter registration drive updates
              </Typography>
              <Typography variant="body2" sx={{ 
                color: 'rgba(255,255,255,0.95)', 
                fontWeight: 500,
                lineHeight: 1.3,
                mt: 0.5
              }}>
                🏗️ Infrastructure development progress
              </Typography>
            </Box>
            
            <Grid container spacing={1} sx={{ mt: 1 }}>
              <Grid item xs={4}>
                <Box textAlign="center">
                  <Typography variant="h6" fontWeight="bold" color="white">
                    12+
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                    Articles
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={4}>
                <Box textAlign="center">
                  <Typography variant="h6" fontWeight="bold" color="white">
                    85%
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                    Positive
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={4}>
                <Box textAlign="center">
                  <Typography variant="h6" fontWeight="bold" color="white">
                    Today
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                    Latest
                  </Typography>
                </Box>
              </Grid>
            </Grid>
            
            <Box sx={{ pt: 1, borderTop: '1px solid rgba(255,255,255,0.2)' }}>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                Click to view detailed news analysis
              </Typography>
            </Box>
          </Stack>
        </Box>
      )
    },
    {
      id: 'economy',
      title: 'Economic Profile',
      icon: <EconomyIcon />,
      color: '#047857',
      gradient: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
      summary: (
        <Box>
          <Stack spacing={2}>
            <Box>
              <Typography variant="h6" fontWeight="bold" color="white">
                Economic Indicators
              </Typography>
            </Box>
            
            <Grid container spacing={1}>
              <Grid item xs={6}>
                <Box textAlign="center">
                  <Typography variant="h6" fontWeight="bold" color="white">
                    ₹{enhancedData.economy?.avgIncome || '18,500'}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                    Avg Income
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={6}>
                <Box textAlign="center">
                  <Typography variant="h6" fontWeight="bold" color="white">
                    {enhancedData.economy?.employmentRate || '64'}%
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                    Employment
                  </Typography>
                </Box>
              </Grid>
            </Grid>
            
            <Box>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)', fontSize: '0.85rem' }}>
                Primary: {enhancedData.economy?.primarySector || 'Agriculture (58%)'}
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)', fontSize: '0.85rem' }}>
                Growth: {enhancedData.economy?.growthRate || '+3.2%'} (YoY)
              </Typography>
            </Box>
          </Stack>
        </Box>
      )
    }
  ];

  return (
    <Box sx={{ p: 4, backgroundColor: 'grey.50', minHeight: '100vh' }}>
      {/* Header Section */}
      <Paper sx={{ p: 4, mb: 4, borderRadius: 3, background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)' }}>
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 2
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar
              src={civiqLogo}
              alt="CivIQ Logo"
              sx={{
                width: 60,
                height: 60,
                borderRadius: 2,
                border: '2px solid #1976d2',
                boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
              }}
            />
            <Box>
              <Typography 
                variant="h4" 
                fontWeight="bold" 
                color="primary.main"
                gutterBottom
              >
                CivIQ Constituency Dashboard
              </Typography>
              <Typography variant="h6" color="text.secondary" fontWeight={500}>
                Real-time Election Data & Analytics Platform
              </Typography>
            </Box>
          </Box>
          
          <Box>
            <Autocomplete
              options={constituencies}
              getOptionLabel={(option) => option.fullName}
              value={constituencies.find(c => c.code === selectedConstituency) || null}
              onChange={handleConstituencyChange}
              isOptionEqualToValue={(option, value) => option.code === value.code && option.state === value.state}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Constituency"
                  variant="outlined"
                  sx={{ minWidth: 300 }}
                />
              )}
              renderOption={(props, option, { index }) => {
                const uniqueKey = `${option.code}-${option.state}-${option.fullName}-${index}`;
                return (
                  <Box component="li" {...props} key={uniqueKey}>
                    <Box>
                      <Typography variant="body1" fontWeight={600}>
                        {option.fullName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {option.code} • {option.state}
                      </Typography>
                    </Box>
                  </Box>
                );
              }}
              loading={loading}
            />
          </Box>
        </Box>
        
        {/* Quick Stats Bar */}
        {enhancedData && (
          <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid rgba(0,0,0,0.1)' }}>
            <Grid container spacing={3}>
              <Grid item xs={6} sm={3}>
                <Box textAlign="center">
                  <Typography variant="h5" fontWeight="bold" color="primary.main">
                    {formatNumber(enhancedData.profile.voters.total)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" fontWeight={500}>
                    Total Voters
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Box textAlign="center">
                  <Typography variant="h5" fontWeight="bold" color="success.main">
                    {enhancedData.voting ? `${enhancedData.voting.turnout.percentage}%` : 'N/A'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" fontWeight={500}>
                    Voter Turnout
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Box textAlign="center">
                  <Typography variant="h5" fontWeight="bold" color="warning.main">
                    {enhancedData.opinionTrends.sentiment.positive}%
                  </Typography>
                  <Typography variant="caption" color="text.secondary" fontWeight={500}>
                    Positive Sentiment
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Box textAlign="center">
                  <Typography variant="h5" fontWeight="bold" color="info.main">
                    {detailedResults?.winner?.party || enhancedData.results?.winner?.party || 'TBD'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" fontWeight={500}>
                    Leading Party
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Box>
        )}
      </Paper>

      {/* Main Dashboard Layout - Left: Tiles, Right: AI Panels */}
      <Grid container spacing={4}>
        {/* Left Side - Constituency Tiles */}
        <Grid item xs={12} lg={7}>
          <Grid container spacing={2}>
            {tiles.map((tile) => (
              <Grid item xs={12} sm={6} key={tile.id}>
                <Card
                  sx={{
                    cursor: 'pointer',
                    height: '240px',
                    background: tile.gradient,
                    color: tile.color,
                    position: 'relative',
                    overflow: 'hidden',
                    borderRadius: 0,
                    border: `2px solid ${tile.color}`,
                    boxShadow: `0 4px 12px ${alpha(tile.color, 0.15)}`,
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: `0 8px 20px ${alpha(tile.color, 0.25)}`,
                      borderColor: tile.color,
                    },
                  }}
                  onClick={() => handleTileClick(tile.id)}
                >
                  <CardHeader
                    avatar={
                      <Avatar 
                        sx={{ 
                          bgcolor: tile.color, 
                          color: 'white',
                          width: 40,
                          height: 40,
                          borderRadius: 0
                        }}
                      >
                        {React.cloneElement(tile.icon, { fontSize: 'small' })}
                      </Avatar>
                    }
                    title={
                      <Typography variant="subtitle1" fontWeight="700" sx={{ color: tile.color, fontSize: '1rem' }}>
                        {tile.title}
                      </Typography>
                    }
                    action={
                      <IconButton 
                        size="small" 
                        sx={{ 
                          color: tile.color,
                          '&:hover': {
                            backgroundColor: alpha(tile.color, 0.1)
                          }
                        }}
                      >
                        <ArrowUpward sx={{ fontSize: 16 }} />
                      </IconButton>
                    }
                    sx={{ pb: 0.5 }}
                  />
                  <CardContent sx={{ 
                    position: 'relative', 
                    zIndex: 1,
                    height: 'calc(100% - 72px)',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    pt: 0,
                    pb: 2
                  }}>
                    <Box sx={{ 
                      fontSize: '0.8rem',
                      color: tile.color,
                      '& .MuiTypography-h5': { fontSize: '1.1rem', color: tile.color },
                      '& .MuiTypography-h6': { fontSize: '0.9rem', color: tile.color },
                      '& .MuiTypography-body2': { fontSize: '0.75rem', color: tile.color },
                      '& .MuiTypography-caption': { fontSize: '0.7rem', color: alpha(tile.color, 0.8) }
                    }}>
                      {tile.summary}
                    </Box>
                    
                    {/* Decorative background element */}
                    <Box
                      sx={{
                        position: 'absolute',
                        top: -10,
                        right: -10,
                        width: 60,
                        height: 60,
                        background: alpha(tile.color, 0.05),
                        zIndex: 0,
                      }}
                    />
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Grid>

        {/* Vertical Divider */}
        <Grid item xs={12} lg="auto" sx={{ display: { xs: 'none', lg: 'block' } }}>
          <Box sx={{
            width: '2px',
            height: '100%',
            background: 'linear-gradient(180deg, transparent 0%, #e0e7ff 20%, #c7d2fe 50%, #a5b4fc 80%, transparent 100%)',
            mx: 2,
            minHeight: '500px'
          }} />
        </Grid>

        {/* Right Side - AI Intelligence Panels */}
        <Grid item xs={12} lg={4.5}>
          <Stack spacing={2} sx={{ height: '100%' }}>
            {/* AI Summary Panel */}
            <Card sx={{ 
              borderRadius: 0, 
              border: '2px solid #667eea',
              background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
              color: '#667eea',
              flex: 1,
              minHeight: '250px',
              boxShadow: `0 4px 12px ${alpha('#667eea', 0.15)}`,
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: `0 8px 20px ${alpha('#667eea', 0.25)}`,
              },
            }}>
              <CardHeader
                avatar={
                  <Avatar sx={{ 
                    bgcolor: '#667eea', 
                    color: 'white',
                    width: 40,
                    height: 40,
                    borderRadius: 0
                  }}>
                    <AIIcon fontSize="small" />
                  </Avatar>
                }
                title={
                  <Typography variant="subtitle1" fontWeight="700" sx={{ color: '#667eea', fontSize: '1rem' }}>
                    AI-Powered Insights
                  </Typography>
                }
                subheader={
                  <Typography variant="caption" sx={{ color: alpha('#667eea', 0.7) }}>
                    Intelligent analysis of constituency data
                  </Typography>
                }
                sx={{ pb: 0.5 }}
              />
              <CardContent sx={{ 
                height: 'calc(100% - 72px)',
                pt: 0,
                pb: 2,
                overflow: 'hidden'
              }}>
                {loadingAiInsights || loadingAiPredictions ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                    <Stack spacing={2} alignItems="center">
                      <CircularProgress size={24} sx={{ color: '#667eea' }} />
                      <Typography variant="caption" sx={{ color: '#667eea' }}>
                        Loading AI insights...
                      </Typography>
                    </Stack>
                  </Box>
                ) : (aiInsights || aiPredictions) ? (
                  <Stack spacing={2} sx={{ maxHeight: '100%', overflowY: 'auto' }}>
                    {aiInsights && (
                      <Box>
                        <Typography variant="body2" fontWeight="600" gutterBottom sx={{ color: '#667eea' }}>
                          AI Analysis Summary
                        </Typography>
                        <Typography variant="caption" sx={{ color: alpha('#667eea', 0.8), display: 'block', lineHeight: 1.4, mb: 1 }}>
                          Constituency: {aiInsights.constituency_name}
                        </Typography>
                        
                        {/* Data Sources */}
                        {aiInsights.data_sources_used && aiInsights.data_sources_used.length > 0 && (
                          <Box sx={{ mb: 1.5 }}>
                            <Typography variant="caption" fontWeight="600" sx={{ color: '#667eea', display: 'block', mb: 0.5 }}>
                              Data Sources:
                            </Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                              {aiInsights.data_sources_used.map((source, index) => (
                                <Chip
                                  key={index}
                                  label={source.replace(/_/g, ' ')}
                                  size="small"
                                  sx={{
                                    height: 20,
                                    fontSize: '0.65rem',
                                    bgcolor: alpha('#667eea', 0.1),
                                    color: '#667eea',
                                    '& .MuiChip-label': { px: 1 }
                                  }}
                                />
                              ))}
                            </Box>
                          </Box>
                        )}

                        {/* Key Insights */}
                        {aiInsights.key_insights && aiInsights.key_insights.length > 0 && (
                          <Box sx={{ mb: 1.5 }}>
                            <Typography variant="caption" fontWeight="600" sx={{ color: '#667eea', display: 'block', mb: 0.5 }}>
                              Key Insights:
                            </Typography>
                            {aiInsights.key_insights.slice(0, 3).map((insight, index) => (
                              <Typography key={index} variant="caption" sx={{ color: alpha('#667eea', 0.8), display: 'block', lineHeight: 1.3, mb: 0.3 }}>
                                • {insight.replace(/\*\*/g, '').trim()}
                              </Typography>
                            ))}
                          </Box>
                        )}

                        {/* Confidence Score */}
                        {aiInsights.confidence_score && (
                          <Box sx={{ mb: 1.5 }}>
                            <Typography variant="caption" fontWeight="600" sx={{ color: '#667eea', display: 'block', mb: 0.5 }}>
                              Analysis Confidence:
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <LinearProgress
                                variant="determinate"
                                value={aiInsights.confidence_score * 100}
                                sx={{
                                  width: 60,
                                  height: 6,
                                  borderRadius: 3,
                                  bgcolor: alpha('#667eea', 0.2),
                                  '& .MuiLinearProgress-bar': {
                                    bgcolor: '#667eea'
                                  }
                                }}
                              />
                              <Typography variant="caption" sx={{ color: '#667eea', fontWeight: 600 }}>
                                {(aiInsights.confidence_score * 100).toFixed(1)}%
                              </Typography>
                            </Box>
                          </Box>
                        )}
                      </Box>
                    )}

                    {aiPredictions && (
                      <Box>
                        <Typography variant="body2" fontWeight="600" gutterBottom sx={{ color: '#667eea' }}>
                          AI Predictions
                        </Typography>
                        {aiPredictions.detailed_analysis?.full_response ? (
                          <Typography variant="caption" sx={{ color: alpha('#667eea', 0.8), lineHeight: 1.4 }}>
                            {aiPredictions.detailed_analysis.full_response.split('\n').slice(0, 3).join(' ').substring(0, 200)}...
                          </Typography>
                        ) : (
                          <Typography variant="caption" sx={{ color: alpha('#667eea', 0.8), lineHeight: 1.4 }}>
                            Analyzing current trends and historical patterns to provide insights...
                          </Typography>
                        )}
                        {aiPredictions.confidence_score && (
                          <Typography variant="caption" sx={{ color: alpha('#667eea', 0.6), display: 'block', mt: 0.5, fontStyle: 'italic' }}>
                            Confidence: {(aiPredictions.confidence_score * 100).toFixed(1)}%
                          </Typography>
                        )}
                      </Box>
                    )}
                  </Stack>
                ) : (
                  <Stack spacing={2} alignItems="center" sx={{ height: '100%', justifyContent: 'center' }}>
                    <AIIcon sx={{ fontSize: 48, color: alpha('#667eea', 0.3) }} />
                    <Typography variant="body2" sx={{ color: alpha('#667eea', 0.7), textAlign: 'center' }}>
                      Get AI-powered insights and predictions for this constituency
                    </Typography>
                    <Button
                      variant="contained"
                      onClick={() => {
                        loadAiInsights();
                      }}
                      disabled={loadingAiInsights || loadingAiPredictions}
                      sx={{
                        bgcolor: '#667eea',
                        color: 'white',
                        '&:hover': {
                          bgcolor: '#5a67d8'
                        },
                        textTransform: 'none',
                        px: 3
                      }}
                    >
                      {loadingAiInsights ? 'Analyzing...' : 'Generate AI Insights'}
                    </Button>
                  </Stack>
                )}
              </CardContent>
            </Card>

            {/* Next Steps Panel */}
            <Card sx={{ 
              borderRadius: 0, 
              border: '2px solid #f093fb',
              background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
              color: '#f093fb',
              flex: 1,
              minHeight: '250px',
              boxShadow: `0 4px 12px ${alpha('#f093fb', 0.15)}`,
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: `0 8px 20px ${alpha('#f093fb', 0.25)}`,
              },
            }}>
              <CardHeader
                avatar={
                  <Avatar sx={{ 
                    bgcolor: '#f093fb', 
                    color: 'white',
                    width: 40,
                    height: 40,
                    borderRadius: 0
                  }}>
                    <NextStepsIcon fontSize="small" />
                  </Avatar>
                }
                title={
                  <Typography variant="subtitle1" fontWeight="700" sx={{ color: '#f093fb', fontSize: '1rem' }}>
                    Recommended Actions
                  </Typography>
                }
                subheader={
                  <Typography variant="caption" sx={{ color: alpha('#f093fb', 0.7) }}>
                    Strategic next steps for stakeholders
                  </Typography>
                }
                sx={{ pb: 0.5 }}
              />
              <CardContent sx={{ 
                height: 'calc(100% - 72px)',
                pt: 0,
                pb: 2,
                overflow: 'hidden'
              }}>
                {loadingAiRecommendations ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                    <Stack spacing={2} alignItems="center">
                      <CircularProgress size={24} sx={{ color: '#f093fb' }} />
                      <Typography variant="caption" sx={{ color: '#f093fb' }}>
                        Generating strategic recommendations...
                      </Typography>
                    </Stack>
                  </Box>
                ) : aiRecommendations && (aiRecommendations.recommendations || aiRecommendations.detailed_analysis) ? (
                  <Stack spacing={1.5} sx={{ maxHeight: '100%', overflowY: 'auto' }}>
                    {/* Display recommendations from the comprehensive analysis */}
                    {aiRecommendations.recommendations && aiRecommendations.recommendations.length > 0 ? (
                      <>
                        {aiRecommendations.recommendations
                          .filter(rec => !rec.includes('**') && rec.trim().length > 10)
                          .slice(0, 6)
                          .map((recommendation, index) => (
                            <Box key={index} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                              <Box sx={{ 
                                width: 6, 
                                height: 6, 
                                borderRadius: '50%', 
                                bgcolor: '#f093fb',
                                mt: 0.5,
                                flexShrink: 0
                              }} />
                              <Typography variant="caption" sx={{ color: alpha('#f093fb', 0.8), lineHeight: 1.4 }}>
                                {recommendation.replace(/^[•\-\*]\s*/, '').trim()}
                              </Typography>
                            </Box>
                          ))}
                        
                        {/* Confidence Score */}
                        {aiRecommendations.confidence_score && (
                          <Box sx={{ mt: 1.5, pt: 1, borderTop: `1px solid ${alpha('#f093fb', 0.2)}` }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                              <Typography variant="caption" sx={{ color: alpha('#f093fb', 0.8), fontWeight: 600 }}>
                                Strategy Confidence:
                              </Typography>
                              <LinearProgress
                                variant="determinate"
                                value={aiRecommendations.confidence_score * 100}
                                sx={{
                                  width: 40,
                                  height: 4,
                                  borderRadius: 2,
                                  bgcolor: alpha('#f093fb', 0.2),
                                  '& .MuiLinearProgress-bar': {
                                    bgcolor: '#f093fb'
                                  }
                                }}
                              />
                              <Typography variant="caption" sx={{ color: '#f093fb', fontWeight: 600, fontSize: '0.65rem' }}>
                                {(aiRecommendations.confidence_score * 100).toFixed(1)}%
                              </Typography>
                            </Box>
                          </Box>
                        )}

                        {/* Analysis Type & Timestamp */}
                        {aiRecommendations.analysis_type && (
                          <Box sx={{ pt: 0.5 }}>
                            <Typography variant="caption" sx={{ color: alpha('#f093fb', 0.6), fontStyle: 'italic', fontSize: '0.65rem' }}>
                              Analysis: {aiRecommendations.analysis_type} • {aiRecommendations.timestamp ? new Date(aiRecommendations.timestamp).toLocaleDateString() : 'Recent'}
                            </Typography>
                          </Box>
                        )}
                      </>
                    ) : aiRecommendations.detailed_analysis?.full_response ? (
                      <>
                        {/* Extract recommendations from full response */}
                        {aiRecommendations.detailed_analysis.full_response
                          .split('\n')
                          .filter(line => line.includes('*') && line.trim().length > 10)
                          .slice(0, 6)
                          .map((recommendation, index) => (
                            <Box key={index} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                              <Box sx={{ 
                                width: 6, 
                                height: 6, 
                                borderRadius: '50%', 
                                bgcolor: '#f093fb',
                                mt: 0.5,
                                flexShrink: 0
                              }} />
                              <Typography variant="caption" sx={{ color: alpha('#f093fb', 0.8), lineHeight: 1.4 }}>
                                {recommendation.replace(/^[•\-\*]\s*/, '').trim()}
                              </Typography>
                            </Box>
                          ))}
                      </>
                    ) : (
                      <Typography variant="caption" sx={{ color: alpha('#f093fb', 0.8), lineHeight: 1.4 }}>
                        Strategic recommendations being processed...
                      </Typography>
                    )}
                  </Stack>
                ) : (
                  <Stack spacing={2} alignItems="center" sx={{ height: '100%', justifyContent: 'center' }}>
                    <NextStepsIcon sx={{ fontSize: 48, color: alpha('#f093fb', 0.3) }} />
                    <Typography variant="body2" sx={{ color: alpha('#f093fb', 0.7), textAlign: 'center' }}>
                      Get AI-powered strategic recommendations and action plans
                    </Typography>
                    <Button
                      variant="contained"
                      onClick={loadAiRecommendations}
                      disabled={loadingAiRecommendations}
                      sx={{
                        bgcolor: '#f093fb',
                        color: 'white',
                        '&:hover': {
                          bgcolor: '#e879f9'
                        },
                        textTransform: 'none',
                        px: 3
                      }}
                    >
                      {loadingAiRecommendations ? 'Generating...' : 'Generate Strategy'}
                    </Button>
                  </Stack>
                )}
              </CardContent>
            </Card>
          </Stack>
        </Grid>
      </Grid>

      {/* Advanced AI Chat Interface - Full Width Bottom Section */}
      <Box sx={{ mt: 4 }}>
        <Card sx={{ 
          borderRadius: 0, 
          border: '2px solid #4facfe',
          background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
          color: '#4facfe',
          overflow: 'hidden',
          boxShadow: `0 4px 12px ${alpha('#4facfe', 0.15)}`,
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            boxShadow: `0 8px 20px ${alpha('#4facfe', 0.25)}`,
          },
        }}>
          <CardHeader
            avatar={
              <Stack direction="row" spacing={1} alignItems="center">
                <Avatar
                  src={civiqLogo}
                  alt="CivIQ Logo"
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: 1,
                    border: '2px solid #4facfe'
                  }}
                />
                <Avatar sx={{ 
                  bgcolor: '#4facfe', 
                  color: 'white',
                  width: 48,
                  height: 48,
                  borderRadius: 0
                }}>
                  <ChatIcon sx={{ fontSize: 28 }} />
                </Avatar>
              </Stack>
            }
            title={
              <Typography variant="h5" fontWeight="bold" sx={{ color: '#2563eb' }}>
                CivIQ AI Assistant
              </Typography>
            }
            subheader={
              <Typography variant="body1" sx={{ color: '#475569' }}>
                Your intelligent companion for deep constituency analysis and political insights
              </Typography>
            }
            action={
              <Stack direction="row" spacing={1}>
                <Chip 
                  label="GPT-4 Powered" 
                  size="small" 
                  sx={{ 
                    bgcolor: alpha('#4facfe', 0.1), 
                    color: '#2563eb',
                    border: `1px solid ${alpha('#4facfe', 0.3)}`,
                    fontWeight: 600
                  }} 
                />
                <Chip 
                  label="Real-time Data" 
                  size="small" 
                  sx={{ 
                    bgcolor: alpha('#4facfe', 0.1), 
                    color: '#2563eb',
                    border: `1px solid ${alpha('#4facfe', 0.3)}`,
                    fontWeight: 600
                  }} 
                />
              </Stack>
            }
            sx={{ pb: 2, borderBottom: `1px solid ${alpha('#4facfe', 0.2)}` }}
          />
          
          <CardContent sx={{ p: 0 }}>
            <Grid container>
              {/* Chat Interface */}
              <Grid item xs={12} lg={8}>
                <Box sx={{ 
                  height: '1000px', 
                  display: 'flex', 
                  flexDirection: 'column',
                  background: alpha('#4facfe', 0.02)
                }}>
                  {/* Chat Messages */}
                  <Box sx={{ 
                    flexGrow: 1, 
                    overflowY: 'auto', 
                    p: 3,
                    background: alpha('#4facfe', 0.01)
                  }}>
                    <Stack spacing={3}>
                      {chatMessages.map((message) => (
                        <Box
                          key={message.id}
                          sx={{
                            display: 'flex',
                            justifyContent: message.type === 'user' ? 'flex-end' : 'flex-start',
                            alignItems: 'flex-start',
                            gap: 2
                          }}
                        >
                          {message.type === 'bot' && (
                            <Avatar sx={{ 
                              width: 36, 
                              height: 36, 
                              bgcolor: alpha('#4facfe', 0.1),
                              border: `2px solid ${alpha('#4facfe', 0.3)}`,
                              borderRadius: 0
                            }}>
                              <BotIcon sx={{ fontSize: 20, color: '#2563eb' }} />
                            </Avatar>
                          )}
                          <Paper
                            elevation={0}
                            sx={{
                              p: 2,
                              maxWidth: '75%',
                              bgcolor: message.type === 'user' 
                                ? alpha('#4facfe', 0.08) 
                                : alpha('#4facfe', 0.05),
                              color: '#4facfe',
                              border: `1px solid ${alpha('#4facfe', 0.2)}`,
                              borderRadius: 2
                            }}
                          >
                            <Typography variant="body1" sx={{ fontSize: '0.95rem', lineHeight: 1.6, color: '#1e293b' }}>
                              {message.content}
                            </Typography>
                            <Typography variant="caption" sx={{ 
                              color: '#64748b', 
                              mt: 1, 
                              display: 'block' 
                            }}>
                              {message.timestamp.toLocaleTimeString()}
                            </Typography>
                          </Paper>
                          {message.type === 'user' && (
                            <Avatar sx={{ 
                              width: 36, 
                              height: 36, 
                              bgcolor: alpha('#4facfe', 0.1),
                              border: `2px solid ${alpha('#4facfe', 0.3)}`,
                              borderRadius: 0
                            }}>
                              <Typography variant="body2" fontWeight="600" sx={{ color: '#2563eb' }}>
                                You
                              </Typography>
                            </Avatar>
                          )}
                        </Box>
                      ))}
                      {chatLoading && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Avatar sx={{ 
                            width: 36, 
                            height: 36, 
                            bgcolor: alpha('#4facfe', 0.1),
                            border: `2px solid ${alpha('#4facfe', 0.3)}`,
                            borderRadius: 0
                          }}>
                            <BotIcon sx={{ fontSize: 20, color: '#4facfe' }} />
                          </Avatar>
                          <Paper elevation={0} sx={{
                            p: 2,
                            bgcolor: alpha('#4facfe', 0.05),
                            border: `1px solid ${alpha('#4facfe', 0.2)}`,
                            borderRadius: 2,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 2
                          }}>
                            <CircularProgress size={16} sx={{ color: '#4facfe' }} />
                            <Typography variant="body1" sx={{ color: '#1e293b' }}>
                              AI is analyzing your query...
                            </Typography>
                          </Paper>
                        </Box>
                      )}
                    </Stack>
                  </Box>

                  {/* Enhanced Chat Input */}
                  <Box sx={{ p: 3, borderTop: `1px solid ${alpha('#4facfe', 0.2)}` }}>
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-end' }}>
                      <TextField
                        fullWidth
                        multiline
                        maxRows={3}
                        value={chatInput}
                        onChange={(e) => setChatInput(e.target.value)}
                        placeholder="Ask about voter demographics, election predictions, comparative analysis, or strategic insights..."
                        variant="outlined"
                        onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && (e.preventDefault(), handleChatSubmit())}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            bgcolor: alpha('#4facfe', 0.05),
                            color: '#4facfe',
                            borderRadius: 2,
                            '& fieldset': {
                              borderColor: alpha('#4facfe', 0.3)
                            },
                            '&:hover fieldset': {
                              borderColor: alpha('#4facfe', 0.5)
                            },
                            '&.Mui-focused fieldset': {
                              borderColor: '#4facfe'
                            }
                          },
                          '& .MuiInputBase-input::placeholder': {
                            color: '#64748b',
                            opacity: 1
                          }
                        }}
                      />
                      <Button
                        variant="contained"
                        onClick={handleChatSubmit}
                        disabled={!chatInput.trim() || chatLoading}
                        sx={{
                          minWidth: 120,
                          height: 56,
                          bgcolor: '#4facfe',
                          color: 'white',
                          borderRadius: 2,
                          fontWeight: 600,
                          '&:hover': {
                            bgcolor: alpha('#4facfe', 0.8)
                          },
                          '&.Mui-disabled': {
                            bgcolor: alpha('#4facfe', 0.3),
                            color: alpha('#4facfe', 0.5)
                          }
                        }}
                        startIcon={<SendIcon />}
                      >
                        Send
                      </Button>
                    </Box>
                    
                    {/* Quick Action Chips */}
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="caption" sx={{ color: '#64748b', mb: 1, display: 'block' }}>
                        Quick Actions:
                      </Typography>
                      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        {[
                          'Analyze voter turnout trends',
                          'Compare with neighboring constituencies',
                          'Predict election outcome',
                          'Demographic deep dive',
                          'Economic impact analysis',
                          'Social media sentiment'
                        ].map((action, index) => (
                          <Chip
                            key={index}
                            label={action}
                            size="small"
                            onClick={() => setChatInput(action)}
                            sx={{
                              bgcolor: alpha('#4facfe', 0.1),
                              color: '#475569',
                              border: `1px solid ${alpha('#4facfe', 0.2)}`,
                              mb: 1,
                              '&:hover': {
                                bgcolor: alpha('#4facfe', 0.2),
                                color: '#1e293b'
                              }
                            }}
                          />
                        ))}
                      </Stack>
                    </Box>
                  </Box>
                </Box>
              </Grid>

              {/* AI Features Sidebar */}
              <Grid item xs={12} lg={4}>
                <Box sx={{ 
                  height: '1000px',
                  p: 3,
                  background: alpha('#4facfe', 0.05),
                  borderLeft: `1px solid ${alpha('#4facfe', 0.2)}`,
                  overflowY: 'auto'
                }}>
                  <Typography variant="h6" fontWeight="bold" sx={{ color: '#2563eb' }} gutterBottom>
                    AI Capabilities
                  </Typography>
                  
                  <Stack spacing={2}>
                    {[
                      {
                        icon: '🔍',
                        title: 'Deep Analysis',
                        desc: 'Comprehensive demographic and voting pattern analysis'
                      },
                      {
                        icon: '📊',
                        title: 'Predictive Insights',
                        desc: 'Election outcome predictions based on historical data'
                      },
                      {
                        icon: '⚡',
                        title: 'Real-time Processing',
                        desc: 'Instant analysis of constituency data and trends'
                      },
                      {
                        icon: '🎯',
                        title: 'Strategic Recommendations',
                        desc: 'Actionable insights for campaign strategies'
                      },
                      {
                        icon: '📈',
                        title: 'Comparative Analysis',
                        desc: 'Cross-constituency comparisons and benchmarking'
                      },
                      {
                        icon: '🌐',
                        title: 'Multi-source Integration',
                        desc: 'Combines election data, news, and social sentiment'
                      }
                    ].map((feature, index) => (
                      <Paper
                        key={index}
                        elevation={0}
                        sx={{
                          p: 2,
                          bgcolor: alpha('#4facfe', 0.03),
                          border: `1px solid ${alpha('#4facfe', 0.15)}`,
                          borderRadius: 2
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                          <Typography variant="h6" sx={{ fontSize: '1.5rem' }}>
                            {feature.icon}
                          </Typography>
                          <Box>
                            <Typography variant="subtitle2" fontWeight="600" sx={{ color: '#2563eb' }} gutterBottom>
                              {feature.title}
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#475569', lineHeight: 1.4 }}>
                              {feature.desc}
                            </Typography>
                          </Box>
                        </Box>
                      </Paper>
                    ))}
                  </Stack>

                  <Divider sx={{ my: 3, borderColor: alpha('#4facfe', 0.2) }} />

                  <Typography variant="h6" fontWeight="bold" sx={{ color: '#2563eb' }} gutterBottom>
                    Data Sources
                  </Typography>
                  
                  <Stack spacing={1}>
                    {[
                      'Election Commission of India',
                      'Census Data 2011-2021',
                      'NewsAPI Real-time Feed',
                      'Social Media Analytics',
                      'Economic Survey Reports',
                      'Historical Election Results'
                    ].map((source, index) => (
                      <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ 
                          width: 6, 
                          height: 6, 
                          borderRadius: '50%', 
                          bgcolor: alpha('#4facfe', 0.6) 
                        }} />
                        <Typography variant="caption" sx={{ color: '#475569' }}>
                          {source}
                        </Typography>
                      </Box>
                    ))}
                  </Stack>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Box>

      {/* Modal Dialog */}
      <Dialog
        open={!!openModal}
        onClose={handleCloseModal}
        maxWidth={openModal === 'news' ? 'xl' : 'lg'}
        fullWidth
        TransitionComponent={Fade}
        TransitionProps={{ timeout: 500 }}
        PaperProps={{
          sx: {
            borderRadius: 0,
            background: 'linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.9) 100%)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255,255,255,0.2)',
            boxShadow: '0 25px 50px rgba(0,0,0,0.15)',
            minHeight: openModal === 'news' ? '80vh' : 'auto'
          }
        }}
      >
        {openModal !== 'news' && (
          <DialogTitle>
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              background: tiles.find(t => t.id === openModal)?.color || '#1976d2',
              color: 'white',
              m: -3,
              mb: 0,
              p: 3,
              borderRadius: 0
            }}>
              <Box>
                <Typography variant="h5" fontWeight="bold">
                  {enhancedData?.profile?.name || 'Constituency'}
                </Typography>
                <Typography variant="subtitle1" sx={{ opacity: 0.9 }}>
                  {tiles.find(t => t.id === openModal)?.title}
                </Typography>
              </Box>
              <IconButton 
                onClick={handleCloseModal}
                sx={{ 
                  color: 'rgba(255,255,255,0.8)',
                  '&:hover': {
                    color: 'white',
                    backgroundColor: 'rgba(255,255,255,0.1)'
                  }
                }}
              >
                <CloseIcon />
              </IconButton>
            </Box>
          </DialogTitle>
        )}
        
        {openModal === 'news' && (
          <IconButton 
            onClick={handleCloseModal}
            sx={{ 
              position: 'absolute',
              right: 8,
              top: 8,
              zIndex: 10,
              color: 'text.secondary',
              backgroundColor: 'rgba(255,255,255,0.9)',
              '&:hover': { backgroundColor: 'rgba(255,255,255,1)' }
            }}
          >
            <CloseIcon />
          </IconButton>
        )}
        <DialogContent sx={{ p: openModal === 'news' ? 0 : 3, pt: openModal === 'news' ? 0 : 3 }}>
          <Zoom in={!!openModal} timeout={300}>
            <Box>
              {renderModalContent()}
            </Box>
          </Zoom>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 0 }}>
          <Button 
            onClick={handleCloseModal} 
            variant="contained"
            size="large"
            sx={{
              borderRadius: 0,
              textTransform: 'none',
              fontWeight: 600,
              px: 4,
              py: 1.5
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ConstituencyDashboard;
